/* eslint-disable prefer-const */
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

import * as actionTypes from '../../actionTypes';

/**
 * @typedef {{
 *   customerId?: string,
 *   latestMessageId?: string,
 *   participantIds?: Array<string>,
 *   messageIds?: Array<string>,
 * } & Omit<import('modules/chat/types').Conversation, 'customer' | 'latestMessage' | 'participants'>} ReducerConversation
 * @typedef {Record<string, ReducerConversation>} ChatDataConversationByIdReducer
 */
/** @type {ChatDataConversationByIdReducer} */
const initialState = {};

const conversationCleaner = conversation => {
  let {
    latestMessage,
    latestMessageId,
    messages,
    messageIds,
    participants,
    participantIds,
    customer,
    customerId,
    ...cleanConversation
  } = conversation;

  return {
    ...cleanConversation,
    ...(customerId || customer?.id ? { customerId: customerId ?? customer?.id } : {}),
    ...(latestMessage?.id || latestMessageId ? { latestMessageId: latestMessage?.id ?? latestMessageId } : {}),
    participantIds: uniq((participants?.map(({ user }) => user.id) ?? []).concat(participantIds ?? [])),
    messageIds: uniq([
      ...(messageIds ?? []),
      ...(latestMessage?.id ? [latestMessage?.id] : []),
      ...(messages?.map(({ id }) => id) ?? []),
    ]),
  };
};

const updateAll = (state, { payload }) => ({
  ...state,
  ...payload.content.reduce(
    (newConversations, conversation) => ({
      ...newConversations,
      [conversation.id]: conversationCleaner({
        ...(state?.[conversation.id] ?? {}),
        ...conversation,
      }),
    }),
    {}
  ),
});

const updateOne = (state, { payload }) => ({
  ...state,
  [payload.id]: conversationCleaner({
    ...(state?.[payload.id] ?? {}),
    ...payload,
  }),
});

const afterPostNewMessage = (state, { payload }) => {
  const alreadyExists = state[payload.conversationId]?.messageIds?.includes(payload.id);
  const messageIds = alreadyExists
    ? state[payload.conversationId].messageIds
    : [payload.id, ...(state[payload.conversationId]?.messageIds ?? [])];
  const latestMessageId = alreadyExists ? state[payload.conversationId].latestMessageId : payload.id;

  return {
    ...state,
    [payload.conversationId]: {
      ...(state[payload.conversationId] ?? {}),
      latestMessageId,
      messageIds,
    },
  };
};

const afterReceiveNewMessage = (state, { payload }) => {
  const { conversationId } = payload.message;
  const prevUnreadCount = state[conversationId]?.unreadCount ?? 0;
  const isNewMessage = !state[conversationId]?.messageIds?.includes(payload.message.id);

  return {
    ...state,
    [conversationId]: {
      ...(state[conversationId] ?? {}),
      latestMessageId: payload.message.id,
      messageIds: uniq([payload.message.id, ...(state[conversationId]?.messageIds ?? [])]),
      unreadCount: isNewMessage ? prevUnreadCount + 1 : prevUnreadCount,
    },
  };
};

const updateMessageIds = (state, { payload }) =>
  payload.content.reduce(
    (acc, { id: messageId, conversationId }) => ({
      ...acc,
      [conversationId]: {
        ...(acc[conversationId] ?? {}),
        messageIds: uniq([messageId, ...(acc[conversationId]?.messageIds ?? [])]),
      },
    }),
    state
  );

/**
 * @param {ChatDataConversationByIdReducer} state
 * @param {import('modules/global/generic.types').Action<import('modules/chat/types').Conversation>} action
 * @returns {ChatDataConversationByIdReducer}
 */
const afterPatchConversation = (state, { payload: conversation }) => ({
  ...state,
  [conversation.id]: conversationCleaner({
    ...(state?.[conversation.id] ?? {}),
    ...conversation,
  }),
});

/**
 * @param {ChatDataConversationByIdReducer} state
 * @param {import("modules/chat/types").SetHistoryIdAction} action
 * @returns {ChatDataConversationByIdReducer}
 */
const clearMessageIdsKeepOldest = (state, action) => {
  const { conversationId } = action.payload;

  return {
    ...state,
    [conversationId]: {
      ...(state[conversationId] ?? {}),
      messageIds: [],
    },
  };
};

export default createReducer(
  initialState,
  {
    [actionTypes.FETCH_CONVERSATIONS_SUCCESS]: updateAll,

    [actionTypes.POST_CONVERSATION_SUCCESS]: updateOne,
    [actionTypes.FETCH_CONVERSATION_SUCCESS]: updateOne,

    [actionTypes.FETCH_MESSAGES_SUCCESS]: updateMessageIds,

    [actionTypes.POST_MESSAGE_SUCCESS]: afterPostNewMessage,
    [actionTypes.RECEIVE_MESSAGE_FROM_SOCKET]: afterReceiveNewMessage,

    [actionTypes.PATCH_CONVERSATION_STATUS_SUCCESS]: afterPatchConversation,

    [actionTypes.SET_HISTORY_ID]: clearMessageIdsKeepOldest,
  }
);
